import { AlertErrorCircle } from '@/assets/icons'
import { ButtonLoader } from '@/components/loaders/ButtonLoader'
import { CreateOrUpdateMatterForm, CreateOrUpdateMatterSchema } from '@/matter/store/schemas'
import { getBrandName } from '@/util/enterprise'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import FloatLabelFormInput from './FloatLabelFormInput'
import FloatLabelFormTextarea from './FloatLabelFormTextArea'
import clsx from 'clsx'
import {
  kBEMatterAlreadyExistsDetail,
  kBEMatterNameReservedDetail,
  kMatterDialogGenericErrorMessage,
  kMatterNameExistsErrorMessage,
  kMatterNameReservedErrorMessage,
  kMaxMatterContextLength,
  kMaxMatterHardLimit,
} from '@/matter/constants'
import MatterCharacterCounter from './MatterCharacterCounter'
import { ApiError } from '@/util/errors/api-error'
import trim from 'lodash/trim'
import { Modal, ModalContent, ModalClose } from '@/components/modal'
import JurisdictionField from '@/jurisdiction-mappings/components/JurisdictionField'
import { JurisdictionKind, useJurisdictionFieldState } from '@/jurisdiction-mappings/jurisdiction-field-state'
import { useSettings } from '@/settings/hooks/useSettings'

type CreateMatterDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (formData: CreateOrUpdateMatterForm) => Promise<void>
}

/**
 * Create Matter Dialog (Controlled)
 *
 * A controlled dialog for creating a new matter.
 * Unlike CreateOrEditMatterDialog, this component is controlled via props
 * and delegates submission handling to the parent.
 */
export default function CreateMatterDialog({ open, onOpenChange, onSubmit }: CreateMatterDialogProps) {
  const brandName = getBrandName()
  const { defaultJurisdiction, getSettings } = useSettings()

  // Local state
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const jurisdictionField = useJurisdictionFieldState(defaultJurisdiction, JurisdictionKind.STATE)

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isValid, isDirty },
    reset,
    setFocus,
    setError: setFieldError,
    watch,
  } = useForm<CreateOrUpdateMatterForm>({
    resolver: zodResolver(CreateOrUpdateMatterSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      client: '',
      description: '',
    },
  })

  // Watch form values for character limit calculation
  const currentValues = watch()

  // Calculate total character count
  const totalCharCount = useMemo(
    () => (currentValues.name?.length || 0) + (currentValues.client?.length || 0) + (currentValues.description?.length || 0),
    [currentValues.name, currentValues.client, currentValues.description]
  )

  // Allow submission if the form is valid, there are actual changes, and under hard limit
  const canSubmit = isValid && isDirty && jurisdictionField.isValid && totalCharCount <= kMaxMatterHardLimit

  const handleFormSubmit = async (formData: CreateOrUpdateMatterForm) => {
    setSubmitError(null)

    const jurisdictionValidationError = jurisdictionField.validate()
    if (jurisdictionValidationError) {
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit({
        ...formData,
        default_jurisdiction: jurisdictionField.value,
      })
    } catch (e) {
      // If there's a 409 Conflict (e.g. duplicate name or reserved name), display the error on the "name" field
      if (e instanceof ApiError && e.status === 409) {
        let errorMessage = kMatterDialogGenericErrorMessage
        if (e.detail === kBEMatterAlreadyExistsDetail) errorMessage = kMatterNameExistsErrorMessage
        else if (e.detail === kBEMatterNameReservedDetail) errorMessage = kMatterNameReservedErrorMessage

        setFieldError('name', {
          type: 'server',
          message: errorMessage,
        })
        setFocus('name')
      } else {
        setSubmitError(kMatterDialogGenericErrorMessage)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset()
      jurisdictionField.reset()
      setSubmitError(null)
    }
    onOpenChange(newOpen)
  }

  useEffect(() => {
    if (!open) return

    getSettings()
    jurisdictionField.reset()
    setSubmitError(null)
    // getSettings is omitted so this runs once when the dialog opens, not once per settings hook render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent
        title="Matter Details"
        subtitle={`Add case details to help ${brandName} understand and assist with this matter.`}
        width="md"
        height="max-lg"
        bodyClassName="overflow-visible"
        preventDismiss
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="space-y-4">
            {/* Matter Name */}
            <FloatLabelFormInput
              id="matter-name"
              label="Matter Name"
              required
              error={formErrors.name?.message}
              registration={register('name', {
                setValueAs: trim,
              })}
            />

            <JurisdictionField
              {...jurisdictionField.fieldState}
              idPrefix="matter-jurisdiction"
              error={jurisdictionField.error ?? undefined}
              onChange={jurisdictionField.onChange}
            />

            {/* Client */}
            <FloatLabelFormInput
              id="matter-client"
              label="Client"
              error={formErrors.client?.message}
              registration={register('client', {
                setValueAs: trim,
              })}
            />

            {/* Description */}
            <FloatLabelFormTextarea
              id="matter-description"
              label="Matter Context"
              error={formErrors.description?.message}
              registration={register('description', {
                setValueAs: trim,
              })}
            />
          </div>

          {/* Character counter */}
          <MatterCharacterCounter totalCharCount={totalCharCount} softLimit={kMaxMatterContextLength} hardLimit={kMaxMatterHardLimit} />

          {/* Error - desktop version */}
          {submitError && (
            <p className="p-2 mt-3 mb-6 hidden sm:flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
              <AlertErrorCircle className="w-3.5 h-3.5" />
              {submitError}
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2.5 sm:mt-3.5">
            <p className="text-sm text-brand-neutral-600">
              <span className="text-red-700">*</span> Required fields
            </p>

            {/* Error - mobile version */}
            {submitError && (
              <p className="p-2 flex sm:hidden items-center gap-1 text-xs text-red-800 rounded bg-red-50">
                <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
                {submitError}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex justify-end items-center gap-3 w-full sm:w-auto">
              <ModalClose asChild>
                <button
                  type="button"
                  className="min-w-20 px-4 py-2 text-sm text-brand-500 font-semibold rounded-md ring-1 ring-inset ring-brand-500 focus:outline-none hocus:bg-brand-neutral-100 flex items-center justify-center w-full sm:w-auto"
                >
                  Cancel
                </button>
              </ModalClose>

              <button
                type="submit"
                disabled={isSubmitting || !canSubmit}
                className={clsx(
                  'min-w-20 px-4 py-2 text-sm text-white font-semibold rounded-md flex items-center justify-center w-full sm:w-auto',
                  !canSubmit ? 'bg-true-gray-300 cursor-not-allowed' : 'bg-brand-500 hocus:bg-brand-800',
                  isSubmitting && 'pr-3.5'
                )}
              >
                Save
                <ButtonLoader loading={isSubmitting} />
              </button>
            </div>
          </div>
        </form>
      </ModalContent>
    </Modal>
  )
}
